// Vue « Routeurs » (#/mikrotik) : parc de routeurs MikroTik supervisés.
//
// Aucun agent sur un routeur : c'est la plateforme qui le sonde en SSH. Un
// routeur est tout de même une ligne `agents` (platform = routeros), donc sa
// fiche est la fiche appareil habituelle (#/devices/{agentId}) et ses alertes
// sont les alertes ordinaires.

import { useCallback, useEffect, useState } from 'react';
import { Router, Plus, AlertTriangle, Search, MonitorUp, Network } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useDashboard } from './dashboardContext.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { parseHash, viewToHash } from '../lib/router.jsx';
import { fmtRelativeFromNow, fmtOnlineStatus, onlineSortValue } from '../lib/format.jsx';
import { useRealtime, useCoalesced } from '../lib/realtime.jsx';
import { ClientPicker } from '../components/ClientPicker.jsx';
import { MikrotikDeviceModal } from '../components/MikrotikDeviceModal.jsx';
import { JumpHostsModal } from '../components/JumpHostsModal.jsx';
import { LaunchButton } from '../lib/launch.jsx';
import { UpdatePill, pollProblem, fmtDateTime } from '../lib/mikrotik.jsx';

const inputS = 'bg-card border border-rule-2 rounded-lg px-2.5 py-1.5 text-fg-2 text-xs outline-none';
const btnF = (active) =>
  `rounded-lg px-3 py-1 text-[11px] cursor-pointer border transition-colors ${
    active
      ? 'bg-blue-500/15 border-blue-500 text-blue-400 font-semibold hover:bg-blue-500/25'
      : 'bg-card border-rule-2 text-muted hover:bg-card-hi hover:text-fg-2 hover:border-rule-3'
  }`;
const thCls = 'px-3 py-2 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule whitespace-nowrap';
const tdCls = 'px-3 py-2.5 text-fg-2 border-b border-sidebar text-[12px]';

const STATUS_FILTERS = [
  ['', 'Tous'],
  ['online', 'En ligne'],
  ['offline', 'Hors ligne'],
  ['problem', 'Accès en défaut'],
];

const pctCls = (p) => (p >= 90 ? 'text-crit' : p >= 75 ? 'text-warn' : 'text-fg-2');

export function MikrotikPage() {
  const { can, navigate } = useDashboard();
  const canAdd = can('ManageProvisioning');
  const canWinbox = can('RemoteConnect');
  const canFilterByClient = can('ViewClients');

  const urlQ = parseHash().query;
  const [filters, setFilters] = useState({
    clientId: urlQ.clientId ?? null,
    search: urlQ.search ?? '',
    updateAvailable: urlQ.updateAvailable === 'true',
    status: STATUS_FILTERS.some(([k]) => k === urlQ.status) ? urlQ.status ?? '' : '',
  });
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showJumps, setShowJumps] = useState(false);
  // Le tri vit dans la page, qui reste montée : la table ne se démonte pas au rechargement.
  const sortCtl = useTableSort('name');
  const { sortRows, headerProps, arrow } = sortCtl;

  // Les filtres serveur (client, recherche, MAJ) partent à l'API ; le statut,
  // calculé côté client, ne demande pas de requête.
  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (filters.clientId) p.set('clientId', filters.clientId);
    if (filters.search.trim()) p.set('search', filters.search.trim());
    if (filters.updateAvailable) p.set('updateAvailable', 'true');
    const qs = p.toString();
    const r = await api.get('/platform/mikrotik' + (qs ? '?' + qs : ''));
    setRows(r || []);
  }, [filters.clientId, filters.search, filters.updateAvailable]);

  // Recherche tapée : attendre la fin de la frappe avant d'interroger.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  // Les événements ne servent qu'à déclencher la relecture scopée (cf. realtime.jsx).
  const refresh = useCoalesced(load, 2000);
  useRealtime('AgentStatusChanged', refresh);
  useRealtime('InventoryUpdated', refresh);
  useRealtime('CommandCompleted', refresh);

  // Filtres dans l'URL (remplacement, pas d'empilement d'historique).
  useEffect(() => {
    const hash = viewToHash('mikrotik', null, null, {
      clientId: filters.clientId,
      search: filters.search,
      updateAvailable: filters.updateAvailable ? 'true' : null,
      status: filters.status,
    });
    if (window.location.hash !== hash) window.history.replaceState(null, '', hash);
  }, [filters]);

  const visible = (rows || []).filter((d) => {
    if (filters.status === 'online') return d.isOnline;
    if (filters.status === 'offline') return !d.isOnline;
    if (filters.status === 'problem') return !!pollProblem(d);
    return true;
  });

  const sorted = sortRows(visible, (d, key) => {
    switch (key) {
      case 'status':
        return onlineSortValue(d.isOnline, d.lastHeartbeat);
      case 'name':
        return d.name || '';
      case 'client':
        return (d.clientName || '') + ' / ' + (d.siteName || '');
      case 'host':
        return d.host || '';
      case 'model':
        return d.model || d.boardName || '';
      case 'ros':
        return d.routerOsVersion || '';
      case 'firmware':
        return d.currentFirmware || '';
      case 'cpu':
        return d.isOnline ? d.cpuPercent : null;
      case 'mem':
        return d.isOnline ? d.memoryPercent : null;
      case 'boot':
        return d.lastBootAt ? new Date(d.lastBootAt).getTime() : null;
      case 'backup':
        return d.lastBackupAt ? new Date(d.lastBackupAt).getTime() : null;
      default:
        return null;
    }
  });

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h1 className="text-[22px] font-bold text-fg inline-flex items-center gap-2">
          <Router size={22} strokeWidth={1.75} /> Routeurs
          {rows && <span className="text-sm font-normal text-muted-2">({visible.length})</span>}
        </h1>
        <div className="flex items-center gap-2">
        {can('ViewDevices') && (
          <button
            onClick={() => setShowJumps(true)}
            className="bg-rule border border-rule-3 rounded-lg px-4 py-2 text-fg-2 cursor-pointer text-xs font-semibold hover:bg-rule-2 inline-flex items-center gap-1.5"
          >
            <Network size={14} strokeWidth={1.75} /> Serveurs de rebond
          </button>
        )}
        {canAdd && (
          <button
            onClick={() => setShowAdd(true)}
            className="bg-blue-500 border-none rounded-lg px-4 py-2 text-white cursor-pointer text-xs font-semibold hover:bg-blue-600 inline-flex items-center gap-1.5"
          >
            <Plus size={14} strokeWidth={2} /> Ajouter un routeur
          </button>
        )}
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2.5 mb-4 flex-wrap">
        <div className="relative">
          <Search size={13} strokeWidth={1.75} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-2" />
          <input
            className={`${inputS} pl-7 w-[230px]`}
            placeholder="Nom, hôte, modèle, série…"
            value={filters.search}
            onChange={(e) => setF({ search: e.target.value })}
          />
        </div>
        {canFilterByClient && (
          <ClientPicker
            className="w-[240px]"
            value={filters.clientId}
            onChange={(id) => setF({ clientId: id || null })}
            placeholder="Tous les clients"
          />
        )}
        <div className="flex gap-1.5">
          {STATUS_FILTERS.map(([k, l]) => (
            <button key={k} className={btnF(filters.status === k)} onClick={() => setF({ status: k })}>
              {l}
            </button>
          ))}
        </div>
        <button className={btnF(filters.updateAvailable)} onClick={() => setF({ updateAvailable: !filters.updateAvailable })}>
          Mise à jour disponible
        </button>
      </div>

      <div className="bg-card border border-rule rounded-2xl overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={thCls} {...headerProps('status')}>
                Statut{arrow('status')}
              </th>
              <th className={thCls} {...headerProps('name')}>
                Nom{arrow('name')}
              </th>
              <th className={thCls} {...headerProps('client')}>
                Client / site{arrow('client')}
              </th>
              <th className={thCls} {...headerProps('host')}>
                Hôte{arrow('host')}
              </th>
              <th className={thCls} {...headerProps('model')}>
                Modèle{arrow('model')}
              </th>
              <th className={thCls} {...headerProps('ros')}>
                RouterOS{arrow('ros')}
              </th>
              <th className={thCls} {...headerProps('firmware')}>
                Firmware{arrow('firmware')}
              </th>
              <th className={thCls} {...headerProps('cpu')}>
                CPU{arrow('cpu')}
              </th>
              <th className={thCls} {...headerProps('mem')}>
                Mémoire{arrow('mem')}
              </th>
              <th className={thCls} {...headerProps('boot')}>
                En service depuis{arrow('boot')}
              </th>
              <th className={thCls} {...headerProps('backup')}>
                Dernière sauvegarde{arrow('backup')}
              </th>
              {/* Collante à droite : sur un écran étroit le tableau défile, et
                  l'action la plus utilisée ne doit pas partir hors champ. */}
              <th className={`${thCls} sticky right-0 bg-card`}></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td colSpan={12} className="p-8 text-center text-muted-2 text-xs">
                  Chargement…
                </td>
              </tr>
            )}
            {rows && sorted.length === 0 && (
              <tr>
                <td colSpan={12} className="p-8 text-center text-muted-2 text-xs">
                  {rows.length === 0 && !filters.clientId && !filters.search && !filters.updateAvailable
                    ? 'Aucun routeur supervisé pour le moment.'
                    : 'Aucun routeur ne correspond aux filtres.'}
                </td>
              </tr>
            )}
            {sorted.map((d) => {
              const problem = pollProblem(d);
              return (
                <tr
                  key={d.agentId}
                  className="hover:bg-card-hi cursor-pointer"
                  onClick={() => navigate('deviceDetail', { device: String(d.agentId) })}
                >
                  <td className={`${tdCls} whitespace-nowrap`}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${d.isOnline ? 'bg-ok' : 'bg-crit'}`} />
                      <span className={d.isOnline ? 'text-ok' : 'text-crit'}>
                        {fmtOnlineStatus(d.isOnline, d.lastHeartbeat)}
                      </span>
                      {problem && (
                        <span
                          title={problem.label + (problem.detail ? ' : ' + problem.detail : '')}
                          className="inline-flex items-center gap-1 text-warn text-[11px]"
                        >
                          <AlertTriangle size={13} strokeWidth={2} /> {problem.label}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className={tdCls}>
                    <a
                      href={'#/devices/' + d.agentId}
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-400 hover:underline font-semibold"
                    >
                      {d.name}
                    </a>
                    {d.activeAlerts > 0 && (
                      <span className="ml-2 text-[10px] font-semibold text-crit" title="Alertes actives">
                        {d.activeAlerts} alerte{d.activeAlerts > 1 ? 's' : ''}
                      </span>
                    )}
                  </td>
                  <td className={tdCls}>
                    <div className="text-fg-2">{d.clientName || '—'}</div>
                    <div className="text-[10px] text-muted-2">{d.siteName}</div>
                  </td>
                  <td className={`${tdCls} font-mono text-[11.5px]`}>{d.host}
                    {d.jumpHostName && (
                      <div className="font-sans text-[10px] text-muted-2" title="Connexion via un serveur de rebond SSH">
                        via {d.jumpHostName}
                      </div>
                    )}
                  </td>
                  <td className={tdCls}>{d.model || d.boardName || '—'}</td>
                  <td className={`${tdCls} whitespace-nowrap`}>
                    <span className="font-mono">{d.routerOsVersion || '—'}</span>{' '}
                    {d.updateAvailable && (
                      <UpdatePill title={'Dernière version du canal ' + (d.updateChannel || '')}>
                        {'MAJ → ' + d.latestVersion}
                      </UpdatePill>
                    )}
                  </td>
                  <td className={`${tdCls} whitespace-nowrap`}>
                    <span className="font-mono">{d.currentFirmware || '—'}</span>{' '}
                    {d.firmwareUpgradeAvailable && (
                      <UpdatePill title={'Firmware disponible : ' + d.upgradeFirmware}>
                        {'MAJ → ' + d.upgradeFirmware}
                      </UpdatePill>
                    )}
                  </td>
                  <td className={`${tdCls} font-mono ${d.isOnline ? pctCls(d.cpuPercent) : 'text-muted-3'}`}>
                    {d.isOnline ? Math.round(d.cpuPercent) + ' %' : '—'}
                  </td>
                  <td className={`${tdCls} font-mono ${d.isOnline ? pctCls(d.memoryPercent) : 'text-muted-3'}`}>
                    {d.isOnline ? Math.round(d.memoryPercent) + ' %' : '—'}
                  </td>
                  <td className={`${tdCls} whitespace-nowrap`} title={d.lastBootAt ? fmtDateTime(d.lastBootAt) : undefined}>
                    {d.lastBootAt ? fmtRelativeFromNow(d.lastBootAt) : '—'}
                  </td>
                  <td
                    className={`${tdCls} whitespace-nowrap ${d.lastBackupError ? 'text-crit' : ''}`}
                    title={d.lastBackupError || (d.lastBackupAt ? fmtDateTime(d.lastBackupAt) : undefined)}
                  >
                    {d.lastBackupError ? (
                      <span className="inline-flex items-center gap-1">
                        <AlertTriangle size={12} strokeWidth={2} />
                        {d.lastBackupAt ? 'il y a ' + fmtRelativeFromNow(d.lastBackupAt) : 'Échec'}
                      </span>
                    ) : d.lastBackupAt ? (
                      'il y a ' + fmtRelativeFromNow(d.lastBackupAt)
                    ) : d.backupEnabled ? (
                      <span className="text-muted-2">Aucune</span>
                    ) : (
                      <span className="text-muted-2">Désactivée</span>
                    )}
                  </td>
                  <td className={`${tdCls} text-right sticky right-0 bg-card`}>
                    {canWinbox && (
                      <LaunchButton
                        path={'/platform/launch/winbox/' + d.agentId}
                        icon={MonitorUp}
                        title="Ouvrir WinBox sur ce routeur (via Fonxtio Tech)"
                        className="bg-rule border border-rule-3 rounded-md px-2.5 py-1 text-fg-2 cursor-pointer text-[11px] font-semibold hover:bg-rule-2 disabled:opacity-60 inline-flex items-center gap-1"
                      >
                        WinBox
                      </LaunchButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showJumps && <JumpHostsModal canEdit={canAdd} onClose={() => setShowJumps(false)} />}

      {showAdd && (
        <MikrotikDeviceModal
          device={null}
          onClose={(saved) => {
            setShowAdd(false);
            if (saved) load();
          }}
        />
      )}
    </div>
  );
}
